import React from 'react';
import clsx from 'clsx';

interface BookingFormLayoutProps {
  /** Main content area (left side on desktop, full width on mobile) */
  children: React.ReactNode;
  /** Sidebar content (right side on desktop, hidden on mobile) */
  sidebar?: React.ReactNode;
  /** Mobile bottom bar content */
  mobileBottomBar?: React.ReactNode;
  /** Additional className for the container */
  className?: string;
}

/**
 * Responsive two-column layout for V4 booking form.
 * - Desktop: Form area (70%) + sticky summary sidebar (30%)
 * - Mobile: Single column with sticky bottom bar
 */
export const BookingFormLayout: React.FC<BookingFormLayoutProps> = ({
  children,
  sidebar,
  mobileBottomBar,
  className,
}) => {
  return (
    <div
      className={clsx(
        'w-full min-h-screen',
        // Mobile: single column with padding for bottom bar
        'pb-20 md:pb-0',
        className,
      )}
    >
      {/* Main container - responsive grid */}
      <div
        className={clsx(
          'w-full',
          // Desktop: two-column layout
          'md:grid md:grid-cols-[1fr_320px] md:gap-6 lg:gap-8',
          // Max width and centering
          'max-w-7xl mx-auto',
          'px-4 md:px-6 lg:px-8',
        )}
      >
        {/* Main content area */}
        <main className="w-full">{children}</main>

        {/* Desktop sidebar - sticky */}
        {sidebar && (
          <aside className="hidden md:block">
            <div className="sticky top-4">{sidebar}</div>
          </aside>
        )}
      </div>

      {/* Mobile bottom bar - fixed at bottom */}
      {mobileBottomBar && (
        <div
          className={clsx(
            'md:hidden',
            'fixed bottom-0 left-0 right-0',
            'bg-white border-t border-gray-200',
            'shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]',
            'z-50',
          )}
        >
          {mobileBottomBar}
        </div>
      )}
    </div>
  );
};

/**
 * Form section wrapper with consistent styling.
 * Uses brand colors: white background with subtle shadow.
 */
interface FormSectionProps {
  children: React.ReactNode;
  className?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({ children, className }) => {
  return (
    <section
      className={clsx(
        'bg-white rounded-lg',
        'shadow-md-tent',
        className,
      )}
    >
      {children}
    </section>
  );
};

/**
 * Form section header.
 * Uses brand fonts: serif (Fraunces) for headings.
 */
interface FormSectionHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export const FormSectionHeader: React.FC<FormSectionHeaderProps> = ({
  title,
  subtitle,
  action,
  className,
}) => {
  return (
    <div
      className={clsx(
        'px-5 py-5 md:px-6 md:py-6',
        'border-b border-dotted border-gray-300',
        'flex items-start justify-between gap-4',
        className,
      )}
    >
      <div>
        <h2 className="text-sm md:text-base font-serif font-semibold text-gray-700 uppercase tracking-wider">
          {title}
        </h2>
        {subtitle && <p className="mt-1.5 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
};

/**
 * Form section content wrapper
 */
interface FormSectionContentProps {
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
}

export const FormSectionContent: React.FC<FormSectionContentProps> = ({
  children,
  className,
  noPadding = false,
}) => {
  return (
    <div className={clsx(!noPadding && 'px-5 py-5 md:px-6 md:py-6', className)}>
      {children}
    </div>
  );
};
